import cc from "classcat"
import { useContext } from "react"
import Slider from "react-slick"
import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"
import { siteCountry } from "utils/env"
import ArrowMore from "images/arrow-more.svg"
import LocalizedLink from "components/LocalizedLink"
import CustomSliderDot from "components/atoms/common/CustomSliderDot"

import countries from "../../../../config/i18n"

const locales = countries[siteCountry] || countries.fi

const Tag = ({ text, theme = "dark" }) => {
  return (
    <p
      className={cc([
        "m-0 my-2 block w-max rounded-full px-4 py-2 text-sm font-bold",
        {
          "bg-kkm-green text-white": theme === "dark",
          "bg-kkm-darkgreen text-white": theme === "light",
          "bg-kkm-yellow text-zinc-800": theme === "green-yellow",
        },
      ])}
    >
      {text}
    </p>
  )
}

const Link = ({ item, children }) => {
  if (item.slug) {
    return <LocalizedLink to={item.slug}>{children}</LocalizedLink>
  }
  if (item.url) {
    return (
      <a href={item.url} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    )
  }
  return null
}

type ListTheme = "dark" | "light" | "green-yellow"

type ListItem = {
  title: string
  publisherName?: string
  tag?: string
  content: any
  slug?: string
  url?: string
  id: string
}

type ItemProps = {
  item: ListItem
  theme: ListTheme
}

const Item = ({ item, theme }: ItemProps) => {
  const { buttonMore } = useTranslations()
  return (
    <div key={item.title} className="flex flex-col pr-6 md:pr-0">
      <Tag text={item.publisherName || item.tag} theme={theme} />
      <div className="mt-2 flex flex-col space-y-3">
        <div className="grow">
          <h3
            className={cc([
              "m-0 mb-4 block text-xl font-medium ",
              {
                "text-white": theme === "dark",
                "text-zinc-800": theme === "light" || theme === "green-yellow",
              },
            ])}
          >
            {item.content}
          </h3>
        </div>
      </div>
      <div
        className={cc([
          "block text-sm font-medium  underline hover:text-teal-600",
          {
            "text-white": theme === "dark",
            "text-kkm-darkgreen": theme === "light",
            "text-zinc-800": theme === "green-yellow",
          },
        ])}
      >
        <Link item={item}>{buttonMore}</Link>
      </div>
    </div>
  )
}

type ListHorizontalProps = {
  items: ListItem[]
  theme?: ListTheme
  allItemsLink?: string
  title?: string
}

export default function ListHorizontal({
  items = [],
  theme = "dark",
  allItemsLink = "/media",
  title,
}: ListHorizontalProps) {
  const { mediaSectionTitle } = useTranslations()
  const { locale } = useContext(LocaleContext)

  const sliderSettings = {
    infinite: true,
    slidesToScroll: 1,
    speed: 1000,
    autoplaySpeed: 3000,
    autoplay: true,
    dots: true,
    arrows: false,
    fade: false,
    customPaging: () => (
      <a>
        <CustomSliderDot
          outerCircleColor={
            theme === "green-yellow" ? "#fff" : "hsl(var(--color-kkm-green)"
          }
        />
      </a>
    ), // li content with slick-active class on it when active, color applied with tailwind.css
  }

  const getDotClass = () => {
    if (theme === "dark") {
      return "slick-dots green-on-black"
    } else if (theme === "light") {
      return "slick-dots green-on-white"
    } else if (theme === "green-yellow") {
      return "slick-dots white-on-green"
    }
    return "slick-dots green-on-black"
  }

  return (
    <div
      className={cc([
        "relative py-16 lg:py-24",
        {
          "bg-zinc-800": theme === "dark",
          "bg-white": theme === "light",
          "bg-kkm-green": theme === "green-yellow",
        },
      ])}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2
          className={cc([
            "mb-8 text-3xl font-extrabold tracking-tight  lg:text-4xl",
            {
              "text-zinc-100": theme === "dark",
              "text-zinc-800": theme === "light",
              "text-white": theme === "green-yellow",
            },
          ])}
        >
          {title || `${locales[locale].defaultTitle} ${mediaSectionTitle}`}
        </h2>
        <div className="flex space-x-12">
          <div className="w-full">
            <div className="hidden grid-cols-1 gap-y-12 lg:grid lg:grid-cols-3 lg:gap-x-16 lg:gap-y-0">
              {items.map((item) => (
                <Item item={item} theme={theme} />
              ))}
            </div>
            <div className="block lg:hidden">
              <Slider
                slidesToShow={1}
                {...sliderSettings}
                dotsClass={getDotClass()}
              >
                {items.map((item) => (
                  <div className="mb-8 ">
                    <Item key={item.id} item={item} theme={theme} />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
          <LocalizedLink
            to={allItemsLink}
            className={cc([
              "flex w-14  cursor-pointer items-center transition  hover:scale-105",
              {
                "text-white hover:text-kkm-green": theme === "dark",
                "text-zinc-800 hover:text-kkm-green": theme === "light",
                "text-white hover:text-kkm-yellow": theme === "green-yellow",
              },
            ])}
          >
            <ArrowMore />
          </LocalizedLink>
        </div>
      </div>
    </div>
  )
}
